import { useState } from 'react';

export default function BookingSystem() {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const timeSlots = [
    '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM',
    '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
    '05:00 PM', '05:30 PM', '06:00 PM'
  ];

  const handleDateClick = (day: number) => {
    const date = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(date);
    setSelectedTime('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBookingData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', bookingData.name);
      formBody.append('email', bookingData.email);
      formBody.append('phone', bookingData.phone);
      formBody.append('serviceType', selectedService);
      formBody.append('bookingDate', selectedDate);
      formBody.append('bookingTime', selectedTime);
      formBody.append('notes', bookingData.notes);

      const response = await fetch('https://readdy.ai/api/form/d5rmii3592clg457kp0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setBookingData({ name: '', email: '', phone: '', notes: '' });
        setSelectedDate('');
        setSelectedTime('');
        setSelectedService('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCalendar = () => {
    const days = [];
    const today = currentDate.getDate();

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isSelected = selectedDate === dateStr;
      const isPast = day < today;
      const isToday = day === today;

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => !isPast && handleDateClick(day)}
          disabled={isPast}
          className={`h-12 rounded-lg text-sm font-medium transition-all cursor-pointer ${
            isSelected
              ? 'bg-uri-teal text-white shadow-lg'
              : isPast
              ? 'text-gray-300 cursor-not-allowed'
              : isToday
              ? 'bg-uri-teal/10 text-uri-teal hover:bg-uri-teal/20'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <section id="booking" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Programe su Servicio
          </h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Reserve una cita con nuestros profesionales de salud a su conveniencia
          </p>
        </div>

        <form id="booking-form" data-readdy-form onSubmit={handleSubmit} className="grid lg:grid-cols-10 gap-8">
          {/* Left Panel - Service Selection */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-gray-50 rounded-2xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4 text-base">Seleccione Tipo de Servicio</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-uri-green transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="serviceType"
                    value="residential-care"
                    checked={selectedService === 'residential-care'}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="w-5 h-5 text-uri-green accent-uri-green"
                  />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Cuidado Residencial</p>
                    <p className="text-xs text-gray-600">Servicios de cuidado en casa</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-uri-blue transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="serviceType"
                    value="hospital-public"
                    checked={selectedService === 'hospital-public'}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="w-5 h-5 text-uri-blue accent-uri-blue"
                  />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Hospital Público</p>
                    <p className="text-xs text-gray-600">Asistencia en instalación pública</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-uri-teal transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="serviceType"
                    value="hospital-private"
                    checked={selectedService === 'hospital-private'}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="w-5 h-5 text-uri-teal accent-uri-teal"
                  />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Centro Privado</p>
                    <p className="text-xs text-gray-600">Cuidado en instalación privada</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4 text-base">Su Información</h4>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={bookingData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="Su nombre"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={bookingData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={bookingData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={bookingData.notes}
                    onChange={handleChange}
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm resize-none"
                    placeholder="Información adicional (máx 500 caracteres)"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* Center - Calendar */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-xl font-bold text-gray-900">
                  {monthNames[currentMonth]} {currentYear}
                </h4>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-time-zone-line"></i>
                  <span>EST (UTC-5)</span>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="mb-6">
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                    <div key={day} className="text-center text-xs font-semibold text-gray-600 h-8 flex items-center justify-center">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {renderCalendar()}
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3 text-sm">Seleccione Hora</h5>
                  <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {timeSlots.map(time => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={`py-2 px-3 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedTime === time
                            ? 'bg-uri-teal text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Summary */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-uri-teal/5 to-uri-green/5 rounded-2xl p-6 sticky top-24">
              <h4 className="font-semibold text-gray-900 mb-4 text-base">Resumen de Reserva</h4>
              
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Servicio</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedService === 'residential-care' && 'Cuidado Residencial'}
                    {selectedService === 'hospital-public' && 'Hospital Público'}
                    {selectedService === 'hospital-private' && 'Centro Privado'}
                    {!selectedService && 'No seleccionado'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Fecha</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedDate || 'No seleccionada'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Hora</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedTime || 'No seleccionada'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Duración</p>
                  <p className="text-sm font-semibold text-gray-900">Consulta Inicial</p>
                </div>
              </div>

              <button
                type="submit"
                disabled={!selectedService || !selectedDate || !selectedTime || isSubmitting}
                className="w-full py-3 bg-uri-teal text-white text-sm font-semibold rounded-lg hover:bg-uri-teal-dark transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Reservando...' : 'Confirmar Reserva'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-uri-green/10 border border-uri-green/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <i className="ri-check-circle-line text-uri-green text-lg flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-uri-green-dark mb-1">¡Solicitud recibida con éxito!</p>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        Su reserva será confirmada tan pronto un agente lo(a) contacte a través de este correo electrónico o los números de teléfono registrados en su solicitud.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-800">Reserva fallida. Por favor intente de nuevo.</p>
                </div>
              )}

              <button
                type="button"
                onClick={() => document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new Event('click'))}
                className="w-full mt-3 py-2 text-uri-teal text-sm font-medium hover:text-uri-teal-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                ¿Necesita ayuda? Chatee con nosotros
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
